import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { cn } from '@/lib/cn';

export function SiteHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on route change (any nav action).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? 'hidden' : original;
    return () => {
      document.body.style.overflow = original;
    };
  }, [mobileOpen]);

  const primary = [
    { to: '/pricing', label: t('nav.pricing') },
    { to: '/compare', label: t('nav.compare') },
    { to: '/demo', label: t('nav.demo') },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-ink-200">
      <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 font-semibold text-ink-900">
          <img src="/logo.png" alt="" width={28} height={28} className="rounded-md" />
          <span className="text-lg tracking-tight serif-jp">{t('brand.name')}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 text-sm">
          {primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'px-3 py-1.5 rounded-md text-ink-600 hover:text-ink-900 hover:bg-ink-100 transition',
                  isActive && 'text-ink-900 bg-ink-100',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop right */}
        <div className="hidden sm:flex items-center gap-2">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-ink-100 transition"
          >
            {t('nav.login')}
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition shadow-soft"
          >
            {t('nav.signup')}
          </Link>
        </div>

        {/* Mobile menu trigger */}
        <button
          type="button"
          aria-label={mobileOpen ? t('nav.close_menu') : t('nav.open_menu')}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav-drawer"
          onClick={() => setMobileOpen((v) => !v)}
          className="sm:hidden inline-flex items-center justify-center h-9 w-9 rounded-md text-ink-700 hover:bg-ink-100 transition"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            id="mobile-nav-drawer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="sm:hidden fixed inset-0 top-16 z-40 bg-white"
          >
            <motion.div
              initial={{ y: -16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.16, 0.84, 0.44, 1] }}
              className="px-6 py-6 border-b border-ink-200"
            >
              <nav className="flex flex-col gap-1 text-base">
                {primary.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      cn(
                        'px-3 py-3 rounded-md text-ink-700 hover:bg-ink-100 transition border-b border-ink-100 last:border-b-0',
                        isActive && 'text-ink-900 bg-ink-100',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>

              <div className="mt-6 flex flex-col gap-3">
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center h-11 px-4 rounded-lg border border-ink-300 text-ink-900 text-sm font-semibold hover:bg-ink-100 transition"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center h-11 px-4 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition shadow-soft"
                >
                  {t('nav.signup')}
                </Link>
              </div>

              <div className="mt-6 pt-6 border-t border-ink-200 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.16em] text-ink-400">
                  {t('common.language')}
                </span>
                <LanguageSwitcher />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
